import React, {useState} from 'react';
import {useQuery} from "react-query";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {prefetchNearbyPages} from "services/query-client";
import {CheckCircleIcon, PuzzleIcon, XCircleIcon} from "@heroicons/react/outline";
import {formattedDateTime} from "services/date";
import Dropdown from 'components/Dropdown';
import {PencilIcon, PlusIcon, TrashIcon} from '@heroicons/react/solid';
import {useNavigate} from "react-router-dom";
import {Achievement} from "types/Achievement";
import DeleteAchievement from "../Delete/DeleteAchievement";
import Button from "../../../components/Button";
import {useOperator} from "../../../context/OperatorContext";

function AchievementList() {
  const navigate = useNavigate();
  const {termFor} = useOperator();

  const [deletingAchievement, setDeletingAchievement] = useState<Achievement | null>(null);
  const [page, setPage] = useState<number>(1);

  const result = useQuery(
    ['achievements.all', {page}],
    () => ngage.achievements.all(page),
    {keepPreviousData: true}
  )

  // Prefetch next and previous pages
  prefetchNearbyPages('achievements.all', page, ngage.achievements.all);

  // Prepare actions
  const actions = (
    <Button icon={PlusIcon} onClick={() => navigate('/achievements/new')}>New</Button>
  )

  // Prepare breadcrumbs
  const breadcrumbs = {
    'App Content': '/',
    'Interactive': '/',
    [termFor('Achievements')]: '/achievements'
  }

  return (
    <Page
      title={termFor('Achievements')}
      subTitle={`Reward based badges and achievements to maximise engagement`}
      breadcrumbs={breadcrumbs}
      actions={actions}
    >
      <Table paginationData={result.data} setPage={setPage}>
        <TableHead>
          <TableRow>
            <TableHeader>Name</TableHeader>
            <TableHeader>Description</TableHeader>
            <TableHeader alignment={TableHeader.alignments.CENTER}>Secret</TableHeader>
            <TableHeader alignment={TableHeader.alignments.CENTER}>Points</TableHeader>
            <TableHeader>Added</TableHeader>
            <TableHeader/>
          </TableRow>
        </TableHead>

        <TableBody isLoading={result.isLoading}>

          {result?.data && result.data.data.length < 1 ?
            <TableRow index={0}>
              <TableData colSpan={6}>
                <div className="py-4 flex flex-col justify-center items-center">
                  <PuzzleIcon className="h-8 w-8 text-gray-400 mb-2"/>
                  <span className="text-sm font-medium text-gray-600">{`No ${termFor('achievements')}`}</span>
                </div>
              </TableData>
            </TableRow>
            : result?.data?.data.map((achievement, index) => (
              <TableRow key={achievement.id} index={index} highlight>
                <TableData>
                  <div className="flex">
                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                      {/* TODO: Convert to component */}
                      <Dropdown position="left">
                        {[
                          {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/achievements/${achievement.id}/edit`)},
                          {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingAchievement(achievement)}
                        ]}
                      </Dropdown>
                    </div>

                    <div className="flex group-hover:translate-x-1 transition-all">
                      <PuzzleIcon className="h-5 w-5 mr-2 text-gray-500"/> {achievement.name}
                    </div>
                  </div>
                </TableData>
                <TableData>{achievement.description}</TableData>
                <TableData alignment={TableData.alignments.CENTER}>
                  {achievement.is_secret ? <CheckCircleIcon className="h-5 w-5 text-green-600 inline"/> :
                    <XCircleIcon className="h-5 w-5 text-red-600 inline"/>}
                </TableData>
                <TableData alignment={TableData.alignments.CENTER}>
                  {achievement.points}
                </TableData>
                <TableData>{formattedDateTime(achievement.created_at)}</TableData>
                <TableData alignment={TableData.alignments.RIGHT}>
                  {/* TODO: Convert to component */}
                  <Dropdown>
                    {[
                      {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/achievements/${achievement.id}/edit`)},
                      {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingAchievement(achievement)}
                    ]}
                  </Dropdown>
                </TableData>
              </TableRow>
            ))}

        </TableBody>
      </Table>

      {/* Delete confirmation modal */}
      <DeleteAchievement
        achievement={deletingAchievement ?? null}
        show={!!deletingAchievement}
        onCancel={() => setDeletingAchievement(null)}
        onDelete={() => {
          setDeletingAchievement(null)
          navigate('/achievements')
        }}
        onDeleteFail={() => setDeletingAchievement(null)}
      />
    </Page>
  );
}

export default AchievementList;